import {
  getNewestOperationForResource,
  type AsyncOperationSummary,
  type AsyncOperationsSummary,
} from '@vrplatform/data';

export type TeamAsyncOperationType = Extract<
  AsyncOperationSummary['type'],
  'team-delete' | 'team-duplicate' | 'vri-team-migration'
>;

const operationLabels = {
  'team-delete': { active: 'Deleting...', failed: 'Deletion failed' },
  'team-duplicate': { active: 'Duplicating...', failed: 'Duplication failed' },
  'vri-team-migration': { active: 'Migrating...', failed: 'Migration failed' },
} as const;

export const getTeamAsyncOperation = (
  team: { id: string; operations?: AsyncOperationsSummary },
  type: TeamAsyncOperationType
) => {
  if (!team.operations) return undefined;

  return getNewestOperationForResource(
    team.operations.items,
    { id: team.id, role: 'target', type: 'team' },
    type
  );
};

export const isTeamAsyncOperationActive = (
  operation: AsyncOperationSummary | undefined
) => operation?.status === 'queued' || operation?.status === 'running';

export const getTeamAsyncOperationDisplay = (team: {
  id: string;
  operations?: AsyncOperationsSummary;
}) => {
  const item = (Object.keys(operationLabels) as TeamAsyncOperationType[])
    .map((type) => ({ operation: getTeamAsyncOperation(team, type), type }))
    .filter(
      (
        candidate
      ): candidate is typeof candidate & {
        operation: NonNullable<typeof candidate.operation>;
      } => candidate.operation !== undefined
    )
    .sort((left, right) =>
      right.operation.createdAt.localeCompare(left.operation.createdAt)
    )[0];

  if (!item) return undefined;

  const { operation, type } = item;

  if (operation.status === 'failed') {
    return {
      color: 'red' as const,
      label: operationLabels[type].failed,
      tooltip: operation.failure?.message ?? 'The operation failed.',
    };
  }

  if (!isTeamAsyncOperationActive(operation)) return undefined;

  return {
    color: type === 'team-delete' ? 'red' : ('blue' as const),
    label: operationLabels[type].active,
  };
};
